import { useQuery } from "@apollo/client/react";
import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useIntl, type IntlShape } from "react-intl";
import { userErrorMessage } from "@/lib/user-error";
import { Link, useNavigate } from "react-router";
import { useMe } from "@/auth/use-me";
import { useHasSystemAccess } from "@/auth/roles";
import { useAssistantTour } from "@/lib/use-assistant-tour";
import { Avatar } from "@/components/common/Avatar";
import { QUICK_ACTION_CLASS, QuickActionLink, StatTile } from "@/components/dashboard/StatTile";
import { NoBackend } from "@/components/common/NoBackend";
import { ShareItemCard } from "@/components/share/ShareItemCard";
import { TobisRecentCard } from "@/pages/tobis/TobisRecentCard";
import { TobisFavoritesRow } from "@/pages/tobis/TobisFavoritesRow";
import { fetchTobisHome, type TobisHomeSummary } from "@/lib/tobis";
import {
  HomeGrowInsightsDocument,
  HomeSaveInsightsDocument,
  MatchListDocument,
  MyselfDocument,
  MySharedItemsDocument,
} from "@/graphql/generated/operations";
import { ELEMENT_CODES } from "@/lib/elements";
import { rankLabel } from "@/lib/grow-rank";
import { setPendingUpload } from "@/lib/pending-upload";
import { POWER_BY_ABBR, POWER_LIBRARY } from "@/lib/power-library";
import { creatorName } from "@/lib/share-item";
import { logApplicationFailure } from "@/lib/user-error";

// HomePage - node-by-node port of workshop's pages/HomePage.tsx. Light
// greeting (random "Hi" / "Hallo" + first name) and four dashboard
// strips (Save · Grow · Match · Share). Save + Grow are NoBackend
// placeholders for now; Match shows the user's draft matches and
// Share shows their published items, both as horizontally-scrolling
// rows.

// Prototyp tobis: die Startseite zeigt in diesem Branch NUR die Tobis-Zeile.
// Save/Grow/Share/Match gehoeren nicht zum Auslieferungsumfang dieser Instanz -
// dort ist Tobis nicht eine Saeule neben vier anderen, sondern das Produkt
// (Marvin, 2026-08-19).
//
// Bewusst ein Schalter und keine geloeschten Bloecke: die vier Bereiche bleiben
// vollstaendig im Code, damit ein Rebase auf main nicht jedes Mal neu entscheiden
// muss, was hier fehlt. Beim Zusammenfuehren mit main ist DIESE Konstante die
// Stelle, die zurueckgedreht werden muss - dann kommen die vier Bereiche und ihre
// Abfragen von selbst wieder.
//
// Der Typ ist explizit boolean und nicht das Literal: mit `true` haelt TypeScript
// den else-Zweig fuer unerreichbar und meldet toten Code.
const NUR_TOBIS: boolean = true;

const HELLOS = ["Hi", "Hallo"] as const;
const HELLO = HELLOS[Math.floor(Math.random() * HELLOS.length)];

interface MatchNode {
  id: string;
  name?: string | null;
  candidateCount?: number | null;
  updatedAt?: string | null;
  createdBy?: { formattedName?: string | null; name?: { given?: string[] | null; family?: string | null } } | null;
}

function shortDate(s: string | null | undefined): string {
  return s ? String(s).slice(0, 10) : "";
}

// "+ Datei hochladen" quick action for the Service tile. The file dialog opens
// here on the click (a valid user gesture); the chosen files are handed to
// ServicePage's own upload flow via setPendingUpload, then we navigate there.
function ServiceUploadAction() {
  const navigate = useNavigate();
  const intl = useIntl();
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <button type="button" onClick={() => inputRef.current?.click()} className={QUICK_ACTION_CLASS}>
        <span className="text-ink/40">+</span>
        {intl.formatMessage({ id: "home.uploadFile", defaultMessage: "Upload file" })}
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          const list = e.target.files;
          if (!list || !list.length) return;
          const files = Array.from(list);
          e.target.value = "";
          setPendingUpload(files);
          navigate("/save/documents");
        }}
      />
    </>
  );
}

// element enum → { abbr, name } via the shared power library (same mapping the
// Grow pages use), so an insight tile can show the localized power name + link.
function powerName(element: string, intl: IntlShape): { abbr: string; name: string } | null {
  const abbr = ELEMENT_CODES[element];
  if (!abbr) return null;
  const p = POWER_BY_ABBR[abbr];
  return p ? { abbr, name: intl.formatMessage({ id: p.nameId }) } : null;
}

function Dashboard({
  title,
  to,
  right,
  graphql,
  anchor,
  children,
}: {
  title: string;
  to: string;
  right?: string;
  graphql?: string;
  /** data-assistant-anchor for the main-page tour (one stop per dashboard). */
  anchor?: string;
  children: ReactNode;
}) {
  const intl = useIntl();
  return (
    <section className="mt-10 first:mt-0" data-graphql={graphql} data-assistant-anchor={anchor}>
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <Link to={to} className="font-serif text-2xl leading-none transition hover:text-accent">
          {title}
        </Link>
        <Link
          to={to}
          className="text-[10px] uppercase tracking-[0.25em] text-ink/45 transition hover:text-ink"
        >
          {right ?? intl.formatMessage({ id: "home.viewAll", defaultMessage: "View all ↗" })}
        </Link>
      </div>
      {children}
    </section>
  );
}

// Horizontal scroll row. Vertical mousewheel above the area becomes
// horizontal scroll (only if the row is actually scrollable horizontally
// and the gesture is vertical-dominant).
function Row({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const onWheel = (e: WheelEvent) => {
      if (el.scrollWidth <= el.clientWidth) return;
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      el.scrollLeft += e.deltaY;
      e.preventDefault();
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);
  return (
    <div ref={ref} className="flex gap-4 overflow-x-auto pb-1">
      {children}
    </div>
  );
}

/**
 * Die Tobis-Zeile laedt ausserhalb von Apollo, weil der Prototyp auf REST sitzt
 * (siehe lib/tobis.ts). Ein Fehler bleibt hier absichtlich still: die Zeile ist
 * eine Beigabe auf einer Seite, die vier andere Bereiche tragen muss - ein
 * abgeschalteter oder klemmender Prototyp darf die Startseite nicht mit einer
 * Fehlermeldung schmuecken. Der Fehler landet trotzdem im Log.
 */
function useTobisHome(aktiv: boolean): TobisHomeSummary | null {
  const [daten, setDaten] = useState<TobisHomeSummary | null>(null);
  useEffect(() => {
    if (!aktiv) return undefined;
    const ac = new AbortController();
    fetchTobisHome(12, ac.signal)
      .then(setDaten)
      .catch(() => {
        if (ac.signal.aborted) return;
        logApplicationFailure("home.tobis");
      });
    return () => ac.abort();
  }, [aktiv]);
  return daten;
}

export function HomePage() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const matchInput: any = { first: 20 };
  const { data: matchData, loading: matchLoading, error: matchError } = useQuery(
    MatchListDocument,
    { variables: { input: matchInput }, skip: NUR_TOBIS },
  );
  const matches = (matchData?.matches?.nodes ?? []) as MatchNode[];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const myInput: any = { first: 20, where: { owner: { eq: true } } };
  const { data: myData, loading: myLoading, error: myError } = useQuery(MySharedItemsDocument, {
    variables: { input: myInput },
    skip: NUR_TOBIS,
  });
  const myItems = myData?.sharedItems?.nodes ?? [];

  const me = useMe();
  const intl = useIntl();

  // Die Tobis-Saeule ist auf Systemzugriff beschraenkt (requireSystemAccess auf
  // allen /tobis-Routen). Ohne diese Bedingung wuerde die Zeile fuer alle
  // anderen leer laden und auf Seiten verlinken, die sofort auf / zurueckwerfen.
  const hatSystemzugriff = useHasSystemAccess();
  const tobis = useTobisHome(hatSystemzugriff);

  // Save + Grow quick-jump insights (lean dashboard queries). Myself is reused
  // for the Profil entry count (deduped with useMe's own Myself, cache-first).
  const saveQ = useQuery(HomeSaveInsightsDocument, { skip: NUR_TOBIS });
  const growQ = useQuery(HomeGrowInsightsDocument, { skip: NUR_TOBIS });
  const { data: myselfData } = useQuery(MyselfDocument, { skip: NUR_TOBIS });

  const saveLoading = saveQ.loading && !saveQ.data;
  // "Service" mirrors ServicePage: documents + curriculum vitaes combined.
  const docCount =
    (saveQ.data?.documents.totalCount ?? 0) + (saveQ.data?.curriculumVitaes.totalCount ?? 0);
  const contactCount = saveQ.data?.myContactCount ?? 0;
  const communityCount = saveQ.data?.personGroups.personGroups.length ?? 0;
  const pins = saveQ.data?.numberOfPinnedItems;
  const pinTotal =
    (pins?.pinnedPersons ?? 0) + (pins?.pinnedProjects ?? 0) + (pins?.pinnedPosts ?? 0);

  // Profil KPI: total real CV entries (consistent with the other count tiles).
  const m = myselfData?.myself;
  const profileEntryCount =
    (m?.experiences?.length ?? 0) +
    (m?.educations?.length ?? 0) +
    (m?.expertises?.length ?? 0) +
    (m?.certifications?.length ?? 0) +
    (m?.languageSkills?.length ?? 0);

  const growLoading = growQ.loading && !growQ.data;
  const ranking = growQ.data?.growRanking;
  const growScore = ranking?.score ?? 0;

  // "Bewertet" mirrors the Power Map's rule exactly: a power counts as rated
  // once its self-assessment is fully answered (answeredQuestionCount ===
  // questionCount), so the home tile and /grow/map never disagree.
  const selfResults = growQ.data?.selfAssessmentResults ?? [];
  const ratedCodes = new Set<string>();
  for (const r of selfResults) {
    const abbr = ELEMENT_CODES[String(r.element)];
    if (abbr && r.questionCount > 0 && r.answeredQuestionCount === r.questionCount) {
      ratedCodes.add(abbr);
    }
  }
  const totalPowers = POWER_LIBRARY.length;
  const openCount = totalPowers - ratedCodes.size;

  const scoredPowers = [...selfResults]
    .filter((r) => typeof r.result === "number")
    .sort((a, b) => (b.result ?? 0) - (a.result ?? 0));
  const strongest = scoredPowers[0];
  const weakest = scoredPowers.length > 1 ? scoredPowers[scoredPowers.length - 1] : undefined;
  const strongestPower = strongest ? powerName(String(strongest.element), intl) : null;
  const weakestPower = weakest ? powerName(String(weakest.element), intl) : null;

  const topContacts = growQ.data?.contactsWithHighestScore ?? [];

  // Guided tour (/tour, and the "main page" section of /tour-all): the four dashboards.
  useAssistantTour({
    label: intl.formatMessage({ id: "home.tour.label", defaultMessage: "Home" }),
    summary: intl.formatMessage({
      id: "home.tour.summary",
      defaultMessage:
        "This is the main page: the latest numbers from every section at a glance. Every tile links to its spot in the app.",
    }),
    // Im tobis-Zuschnitt gibt es nur einen Anker, und eine Tour, die auf nicht
    // gerenderte Abschnitte zeigt, laeuft ins Leere statt zu fuehren.
    stops: NUR_TOBIS
      ? [
          {
            anchor: "home.tobis",
            title: intl.formatMessage({ id: "home.tour.tobis.title", defaultMessage: "Movie" }),
            description: intl.formatMessage({
              id: "home.tour.tobis.desc",
              defaultMessage:
                "What the last import brought in: titles, people and the market, each tile linking to its list.",
            }),
          },
        ]
      : [
      {
        anchor: "home.save",
        title: intl.formatMessage({ id: "home.tour.save.title", defaultMessage: "Save" }),
        description: intl.formatMessage({
          id: "home.tour.save.desc",
          defaultMessage: "Your profile entries, documents, contacts and pins, with quick actions to add new ones.",
        }),
      },
      {
        anchor: "home.grow",
        title: intl.formatMessage({ id: "home.tour.grow.title", defaultMessage: "Grow" }),
        description: intl.formatMessage({
          id: "home.tour.grow.desc",
          defaultMessage: "Your profile progress, strongest powers and open self-assessments.",
        }),
      },
      {
        anchor: "home.share",
        title: intl.formatMessage({ id: "home.tour.share.title", defaultMessage: "Share" }),
        description: intl.formatMessage({
          id: "home.tour.share.desc",
          defaultMessage: "Your posts and projects and what is happening around them.",
        }),
      },
      {
        anchor: "home.match",
        title: intl.formatMessage({ id: "home.tour.match.title", defaultMessage: "Match" }),
        description: intl.formatMessage({
          id: "home.tour.match.desc",
          defaultMessage: "Your matches. Click one to continue where you left off.",
        }),
      },
      ],
  });

  return (
    <section className="max-w-[1600px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10">
      <header className="mb-8" data-graphql="Myself, MyAvatar">
        <h1 className="font-serif text-4xl leading-tight tracking-tight @sm:text-5xl">
          {HELLO}
          {me.firstName ? `, ${me.firstName}` : ""}
        </h1>
        <p className="mt-2 text-sm text-ink/55">
          {intl.formatMessage({ id: "home.greeting", defaultMessage: "Glad you're here." })}
        </p>
      </header>

      {NUR_TOBIS ? null : (
        <>
        <Dashboard title="Save" to="/save" graphql="HomeSaveInsights" anchor="home.save"> {/* i18n-allow: SAVE brand name */}
          <div className="grid grid-cols-2 items-start gap-3 @sm:grid-cols-4">
            <StatTile
              to="/save/basics"
              label={intl.formatMessage({ id: "home.profile", defaultMessage: "Profile" })}
              value={profileEntryCount}
              sub={intl.formatMessage(
                profileEntryCount
                  ? { id: "home.entries", defaultMessage: "entries" }
                  : { id: "home.profileEmpty", defaultMessage: "nothing recorded yet" },
              )}
              loading={!myselfData}
              actions={
                <>
                  <QuickActionLink to="/save/basics?add=experience">
                    {intl.formatMessage({ id: "home.workExperience", defaultMessage: "Work experience" })}
                  </QuickActionLink>
                  <QuickActionLink to="/save/basics?add=education">
                    {intl.formatMessage({ id: "home.education", defaultMessage: "Education" })}
                  </QuickActionLink>
                  <QuickActionLink to="/save/basics?add=certificate">
                    {intl.formatMessage({ id: "home.certificate", defaultMessage: "Certificate" })}
                  </QuickActionLink>
                </>
              }
            />
            <StatTile
              to="/save/documents"
              label={intl.formatMessage({ id: "home.service", defaultMessage: "Documents" })}
              value={docCount}
              sub={intl.formatMessage(
                docCount
                  ? { id: "home.documents", defaultMessage: "Documents" }
                  : { id: "home.serviceEmpty", defaultMessage: "nothing filed yet" },
              )}
              loading={saveLoading}
              actions={<ServiceUploadAction />}
            />
            <StatTile
              to="/save/contact"
              label={intl.formatMessage({ id: "home.community", defaultMessage: "Community" })}
              value={contactCount}
              sub={
                communityCount
                  ? intl.formatMessage(
                      { id: "home.communitiesSub", defaultMessage: "{count, plural, one {# community} other {# communities}}" },
                      { count: communityCount },
                    )
                  : intl.formatMessage(
                      contactCount
                        ? { id: "home.contacts", defaultMessage: "Contacts" }
                        : { id: "home.noContacts", defaultMessage: "no contacts yet" },
                    )
              }
              loading={saveLoading}
              actions={
                <>
                  <QuickActionLink to="/save/contact" icon="→">
                    {intl.formatMessage({ id: "home.searchCommunities", defaultMessage: "Search communities" })}
                  </QuickActionLink>
                  <QuickActionLink to="/save/contact?create=group">
                    {intl.formatMessage({ id: "home.community", defaultMessage: "Community" })}
                  </QuickActionLink>
                </>
              }
            />
            <StatTile
              to="/save/pinboard"
              label={intl.formatMessage({ id: "home.pins", defaultMessage: "Pins" })}
              value={pinTotal}
              sub={
                pinTotal ? (
                  <>
                    <Link to="/save/pinboard?type=person" className="hover:text-accent hover:underline">
                      {intl.formatMessage(
                        { id: "home.personsCount", defaultMessage: "{count, plural, one {# person} other {# people}}" },
                        { count: pins?.pinnedPersons ?? 0 },
                      )}
                    </Link>
                    {" · "}
                    <Link to="/save/pinboard?type=project" className="hover:text-accent hover:underline">
                      {intl.formatMessage(
                        { id: "home.projectsCount", defaultMessage: "{count, plural, one {# project} other {# projects}}" },
                        { count: pins?.pinnedProjects ?? 0 },
                      )}
                    </Link>
                    {" · "}
                    <Link to="/save/pinboard?type=post" className="hover:text-accent hover:underline">
                      {intl.formatMessage(
                        { id: "home.postsCount", defaultMessage: "{count, plural, one {# post} other {# posts}}" },
                        { count: pins?.pinnedPosts ?? 0 },
                      )}
                    </Link>
                  </>
                ) : (
                  intl.formatMessage({ id: "home.noPins", defaultMessage: "nothing pinned yet" })
                )
              }
              loading={saveLoading}
              actions={
                <>
                  <QuickActionLink to="/save/pinboard" icon="→">
                    {intl.formatMessage({ id: "home.searchGroups", defaultMessage: "Search groups" })}
                  </QuickActionLink>
                  <QuickActionLink to="/save/pinboard?create=group">
                    {intl.formatMessage({ id: "home.group", defaultMessage: "Group" })}
                  </QuickActionLink>
                </>
              }
            />
          </div>
        </Dashboard>

        <Dashboard title="Grow" to="/grow" graphql="HomeGrowInsights" anchor="home.grow"> {/* i18n-allow: GROW brand name */}
          <div className="grid grid-cols-2 items-start gap-3 @sm:grid-cols-4">
            <StatTile
              to="/grow"
              label={intl.formatMessage({ id: "home.profileProgress", defaultMessage: "Profile progress" })}
              value={`${growScore} %`}
              bar={growScore}
              sub={
                ranking
                  ? rankLabel(ranking.userRank, intl.locale)
                  : intl.formatMessage({ id: "home.notStarted", defaultMessage: "not started yet" })
              }
              loading={growLoading}
              actions={
                <QuickActionLink to="/grow/map" icon="→">
                  {intl.formatMessage({ id: "home.viewPowerMap", defaultMessage: "Show Power Map" })}
                </QuickActionLink>
              }
            />
            <StatTile
              to="/grow/map"
              label={intl.formatMessage({ id: "home.rated", defaultMessage: "Rated" })}
              value={`${ratedCodes.size}/${totalPowers}`}
              sub={
                openCount
                  ? intl.formatMessage(
                      { id: "home.openCount", defaultMessage: "{count} open" },
                      { count: openCount },
                    )
                  : intl.formatMessage({ id: "home.allRated", defaultMessage: "all rated" })
              }
              loading={growLoading}
              actions={
                <QuickActionLink to="/grow/map?unrated=1" icon="→">
                  {intl.formatMessage({ id: "home.viewUnrated", defaultMessage: "Show unrated" })}
                </QuickActionLink>
              }
            />
            <StatTile
              to={strongestPower ? `/grow/power/${strongestPower.abbr}` : "/grow/map"}
              label={intl.formatMessage({ id: "home.powerVersus", defaultMessage: "Power Versus" })}
              value={
                strongestPower ? (
                  <>
                    {strongest?.result ?? "–"}
                    {weakestPower ? (
                      <>
                        {" "}
                        <span className="text-lg text-ink/40">vs.</span> {weakest?.result ?? "–"} {/* i18n-allow: numeric comparison separator */}
                      </>
                    ) : null}
                  </>
                ) : (
                  "–"
                )
              }
              sub={
                strongestPower ? (
                  // truncate keeps it one line so the tile stays the same height
                  // as the others; the value above is "stärkste vs schwächste".
                  <span className="block truncate">
                    {intl.formatMessage(
                      { id: "home.strongest", defaultMessage: "Strongest: {name}" },
                      { name: strongestPower.name },
                    )}
                    {weakestPower
                      ? intl.formatMessage(
                          { id: "home.weakestSuffix", defaultMessage: " vs. Weakest: {name}" },
                          { name: weakestPower.name },
                        )
                      : ""}
                  </span>
                ) : (
                  intl.formatMessage({ id: "home.notRatedYet", defaultMessage: "not rated yet" })
                )
              }
              loading={growLoading}
              actions={
                strongestPower ? (
                  <>
                    <QuickActionLink to={`/grow/power/${strongestPower.abbr}`} icon="→">
                      {intl.formatMessage(
                        { id: "home.viewPower", defaultMessage: "Show {name}" },
                        { name: strongestPower.name },
                      )}
                    </QuickActionLink>
                    {weakestPower ? (
                      <QuickActionLink to={`/grow/power/${weakestPower.abbr}`} icon="→">
                        {intl.formatMessage(
                          { id: "home.viewPower", defaultMessage: "Show {name}" },
                          { name: weakestPower.name },
                        )}
                      </QuickActionLink>
                    ) : null}
                  </>
                ) : (
                  <QuickActionLink to="/grow/map" icon="→">
                    {intl.formatMessage({ id: "home.viewPowerMap", defaultMessage: "Show Power Map" })}
                  </QuickActionLink>
                )
              }
            />
            <div className="box min-h-32 bg-surface p-4">
              <div className="text-[10px] tracking-wide text-ink/45">
                {intl.formatMessage({ id: "home.inComparison", defaultMessage: "In comparison · Community" })}
              </div>
              {topContacts.length > 0 ? (
                <ol className="mt-2 space-y-0.5">
                  {topContacts.map((c, i) => (
                    <li key={c.person.id}>
                      <Link
                        to={`/p/${c.person.id}`}
                        title={intl.formatMessage(
                          { id: "home.openProfileOf", defaultMessage: "Open {name}" },
                          {
                            name:
                              c.person.formattedName ??
                              intl.formatMessage({ id: "home.profileFallback", defaultMessage: "Profile" }),
                          },
                        )}
                        className="flex items-center gap-2 rounded-md px-1 py-1 transition hover:bg-ink/[0.06]"
                      >
                        <span className="w-3 shrink-0 text-[11px] tabular-nums text-ink/40">{i + 1}</span>
                        <Avatar
                          size="sm"
                          person={{
                            name: c.person.formattedName ?? "",
                            avatarContent: c.person.avatar?.content,
                            avatarMime: c.person.avatar?.mimeType,
                          }}
                        />
                        <span className="min-w-0 flex-1 truncate text-[12px]">
                          {c.person.formattedName ?? "–"}
                        </span>
                        <span className="shrink-0 text-[11px] tabular-nums text-ink/55">
                          {c.score}&thinsp;%
                        </span>
                      </Link>
                    </li>
                  ))}
                </ol>
              ) : (
                <div className="mt-2 text-[11px] text-ink/55">
                  {intl.formatMessage({ id: "home.noCommunity", defaultMessage: "No community yet." })}
                </div>
              )}
            </div>
          </div>
        </Dashboard>

        <Dashboard
          title="Share" // i18n-allow: SHARE brand name
          to="/share"
          anchor="home.share"
          right={intl.formatMessage({ id: "home.myPosts", defaultMessage: "My posts ↗" })}
          graphql="MySharedItems"
        >
          {myLoading ? (
            <div className="text-[11px] text-ink/45">
              {intl.formatMessage({ id: "common.loading", defaultMessage: "Loading…" })}
            </div>
          ) : myError ? (
            <div className="text-[11px] text-ink/45">
              {intl.formatMessage(
                { id: "common.errorWithMessage", defaultMessage: "Error: {message}" },
                { message: userErrorMessage(intl, myError) },
              )}
            </div>
          ) : myItems.length === 0 ? (
            <NoBackend
              label={intl.formatMessage({ id: "home.noOwnPosts", defaultMessage: "No posts of your own yet" })}
              hint={intl.formatMessage({ id: "home.noOwnPostsHint", defaultMessage: "create a project or a post" })}
            />
          ) : (
            <Row>
              {myItems.map((p, i) => (
                <Link
                  key={p.id}
                  to={`/share/discover/${p.id}`}
                  className="block w-64 shrink-0 transition hover:-translate-y-0.5"
                >
                  <ShareItemCard item={p} index={i} />
                </Link>
              ))}
            </Row>
          )}
        </Dashboard>

        <Dashboard
          title="Match" // i18n-allow: MATCH brand name
          to="/match"
          anchor="home.match"
          right={intl.formatMessage({ id: "home.allMatches", defaultMessage: "All matches ↗" })}
          graphql="MatchList"
        >
          {matchLoading ? (
            <div className="text-[11px] text-ink/45">
              {intl.formatMessage({ id: "common.loading", defaultMessage: "Loading…" })}
            </div>
          ) : matchError ? (
            <div className="text-[11px] text-ink/45">
              {intl.formatMessage(
                { id: "common.errorWithMessage", defaultMessage: "Error: {message}" },
                { message: userErrorMessage(intl, matchError) },
              )}
            </div>
          ) : matches.length === 0 ? (
            <NoBackend label={intl.formatMessage({ id: "home.noMatches", defaultMessage: "No matches" })} />
          ) : (
            <Row>
              {matches.map((m) => (
                <Link
                  key={m.id}
                  to={`/match/${m.id}`}
                  className="box w-64 shrink-0 bg-surface p-4 transition hover:-translate-y-0.5 hover:ring-ink/15"
                >
                  <div className="text-[10px] tracking-wide text-ink/45">
                    {intl.formatMessage({ id: "home.draft", defaultMessage: "Draft" })}
                  </div>
                  <div className="mt-1 text-sm font-medium leading-tight line-clamp-2">
                    {m.name || intl.formatMessage({ id: "home.unnamedMatch", defaultMessage: "Unnamed match" })}
                  </div>
                  <div className="mt-1 line-clamp-1 text-[11px] text-ink/55">
                    {creatorName(m.createdBy) || m.createdBy?.formattedName || "–"}
                  </div>
                  <div className="mt-4 text-[10px] text-ink/45">
                    {[
                      shortDate(m.updatedAt),
                      intl.formatMessage(
                        { id: "home.candidatesCount", defaultMessage: "{count, plural, one {# candidate} other {# candidates}}" },
                        { count: m.candidateCount ?? 0 },
                      ),
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                </Link>
              ))}
            </Row>
          )}
        </Dashboard>
        </>
      )}

      {hatSystemzugriff ? (
        <Dashboard
          title={intl.formatMessage({ id: "home.tobis.title", defaultMessage: "Movie" })}
          to="/tobis/titles"
          anchor="home.tobis"
          right={intl.formatMessage({ id: "home.tobis.viewAll", defaultMessage: "All titles \u2197" })}
        >
          <div className="grid grid-cols-1 items-start gap-3 @sm:grid-cols-3">
            <StatTile
              to="/tobis/titles?sort=updated"
              label={intl.formatMessage({ id: "home.tobis.titles", defaultMessage: "Titles" })}
              value={tobis ? intl.formatNumber(tobis.titles.today) : 0}
              sub={intl.formatMessage(
                { id: "home.tobis.updatedToday", defaultMessage: "updated today \u00b7 {week} in 7 days" },
                { week: intl.formatNumber(tobis?.titles.week ?? 0) },
              )}
              loading={!tobis}
            />
            <StatTile
              to="/tobis/people?sort=updated"
              label={intl.formatMessage({ id: "home.tobis.people", defaultMessage: "People" })}
              value={tobis ? intl.formatNumber(tobis.talents.today) : 0}
              sub={intl.formatMessage(
                { id: "home.tobis.updatedToday", defaultMessage: "updated today \u00b7 {week} in 7 days" },
                { week: intl.formatNumber(tobis?.talents.week ?? 0) },
              )}
              loading={!tobis}
            />
            <StatTile
              to="/tobis/market"
              label={intl.formatMessage({ id: "home.tobis.market", defaultMessage: "Market" })}
              value={
                tobis?.market.tvDay
                  ? intl.formatDate(new Date(`${tobis.market.tvDay}T12:00:00Z`), {
                      day: "2-digit",
                      month: "2-digit",
                    })
                  : "\u2013"
              }
              sub={intl.formatMessage(
                {
                  id: "home.tobis.marketSub",
                  defaultMessage: "latest TV day \u00b7 {days, plural, one {# day} other {# days}} on record",
                },
                { days: tobis?.market.tvDays ?? 0 },
              )}
              loading={!tobis}
            />
          </div>

          {/* Die Merkliste steht VOR "was zuletzt hereinkam": sie ist die
              einzige Zeile auf dieser Seite, die der Nutzer selbst gefuellt
              hat - der Ingest-Nachschub ist Kontext dazu, nicht umgekehrt. */}
          <div className="mt-4">
            <TobisFavoritesRow />
          </div>

          {tobis && tobis.recentTitles.length > 0 ? (
            <div className="mt-4">
              {/* Seit die Merkliste darueber steht, brauchen beide Zeilen eine
                  Beschriftung - ohne sie las sich die zweite Zeile wie eine
                  Fortsetzung der ersten. */}
              <div className="mb-2 text-[12px] text-ink/45">
                {intl.formatMessage({ id: "home.tobis.recent", defaultMessage: "Just arrived" })}
              </div>
              <Row>
                {tobis.recentTitles.map((t, i) => (
                  <TobisRecentCard key={`${t.source}:${t.id}`} title={t} index={i} />
                ))}
              </Row>
            </div>
          ) : null}
        </Dashboard>
      ) : null}
    </section>
  );
}
